import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, ChevronDown, ChevronRight, RotateCcw, Inbox, ShieldOff, ShoppingBag, Wallet, Undo2, GitCompareArrows } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SiteInfo from '../components/Attribution/SiteInfo';
import FunnelSection from '../components/Attribution/FunnelSection';

// Birinci taraf, tekilleştirilmiş sipariş kaynağı takibi (hosgelsin).
// Backend: glowytics-backend/src/routes/attribution.js (GET /orders, GET /summary).
const API = 'https://api.glowytics.com';
const BASE = `${API}/api/attribution`;
const PAGE_SIZE = 25;

const CHANNELS = {
  paid_search: { label: 'Ücretli arama', color: '#4f46e5' },
  paid_social: { label: 'Ücretli sosyal', color: '#db2777' },
  organic_search: { label: 'Organik arama', color: '#059669' },
  organic_social: { label: 'Organik sosyal', color: '#0891b2' },
  ai: { label: 'Yapay zekâ', color: '#7c3aed' },
  referral: { label: 'Yönlendirme', color: '#d97706' },
  email: { label: 'E-posta', color: '#ea580c' },
  direct: { label: 'Doğrudan', color: '#6b7280' },
  internal: { label: 'Site içi', color: '#9ca3af' },
  other: { label: 'Diğer', color: '#a1a1aa' },
};
const chLabel = (c) => (CHANNELS[c || 'direct']?.label) || c || 'Doğrudan';
const chColor = (c) => (CHANNELS[c || 'direct']?.color) || '#a1a1aa';

const ORIGIN = {
  persistent: { label: 'Kalıcı', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  session: { label: 'Oturum', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  direct: { label: 'Doğrudan', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};
const CONSENT = {
  granted: { label: 'İzinli', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  denied: { label: 'İzinsiz', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  unknown: { label: 'Bilinmiyor', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};
const CANCELLED = ['cancelled', 'refunded', 'failed', 'trash'];
const STATUS_TR = {
  cancelled: 'İptal', refunded: 'İade', failed: 'Başarısız', trash: 'Silindi', completed: 'Tamamlandı',
  processing: 'Hazırlanıyor', 'on-hold': 'Beklemede', pending: 'Ödeme bekliyor',
};

const nf = new Intl.NumberFormat('tr-TR');
const money = (v, cur = 'TRY') => {
  try { return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cur || 'TRY', maximumFractionDigits: 0 }).format(v || 0); }
  catch (e) { return `${nf.format(Math.round(v || 0))} ₺`; }
};
const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  return isNaN(d) ? '-' : d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const ymd = (d) => new Date(d.getTime() + 3 * 3600e3).toISOString().slice(0, 10);
const daysAgo = (n) => ymd(new Date(Date.now() - n * 86400e3));
const pct = (a, b) => (b > 0 ? `%${((a / b) * 100).toFixed(1).replace('.', ',')}` : '-');
const smc = (o) => [o?.source, o?.medium].filter(Boolean).join(' / ') || '-';

const Badge = ({ cls, children, title }) => (
  <span title={title} className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-medium whitespace-nowrap ${cls}`}>{children}</span>
);

function SourceCell({ ch, o, muted }) {
  if (!ch && !o?.source) return <span className="text-gray-400">-</span>;
  return (
    <div className="min-w-[130px]">
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: chColor(ch) }} />
        <span className={`text-xs font-medium ${muted ? 'text-gray-600' : 'text-gray-900'}`}>{chLabel(ch)}</span>
      </div>
      <div className="text-[11px] text-gray-500 truncate max-w-[190px]" title={smc(o)}>{smc(o)}</div>
      {o?.campaign ? <div className="text-[11px] text-gray-400 truncate max-w-[190px]" title={o.campaign}>{o.campaign}</div> : null}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone = 'text-gray-900' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-1 min-w-0">
      <div className="flex items-center gap-2 text-xs text-gray-500"><Icon size={14} />{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${tone}`}>{value}</div>
      {sub ? <div className="text-[11px] text-gray-500 leading-snug">{sub}</div> : null}
    </div>
  );
}

function Detail({ o }) {
  const rows = [
    ['Atanan kaynak', `${chLabel(o.final?.channel)} · ${smc(o.final)}${o.final?.campaign ? ` · ${o.final.campaign}` : ''}`],
    ['A giriş sayfası (kalıcı)', o.a?.landing_page],
    ['A tıklama kimliği tipi', o.a?.click_id_type],
    ['A dokunuş zamanı', o.a?.touch_at ? fmtDate(o.a.touch_at) : null],
    ['B giriş sayfası (oturum)', o.b?.landing_page],
    ['B referrer', o.b?.referrer],
    ['İlk dokunuş', o.first?.channel ? `${chLabel(o.first.channel)} · ${smc(o.first)}${o.first.campaign ? ` · ${o.first.campaign}` : ''}` : null],
    ['İlk dokunuş zamanı', o.first?.touch_at ? fmtDate(o.first.touch_at) : null],
    ['Oturum / sayfa görüntüleme', `${o.session_count ?? 0} / ${o.pageview_count ?? 0}`],
    ['Ziyaretçi', o.visitor_id],
  ];
  return (
    <div className="grid md:grid-cols-2 gap-4 p-4 bg-gray-50 border-t border-gray-200">
      <dl className="space-y-1.5 text-xs min-w-0">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-3">
            <dt className="w-44 flex-shrink-0 text-gray-500">{k}</dt>
            <dd className="text-gray-900 break-all min-w-0">{v || '-'}</dd>
          </div>
        ))}
      </dl>
      <div className="min-w-0">
        <div className="text-xs text-gray-500 mb-1">WooCommerce kaynak kaydı (ham)</div>
        {o.woo?.raw ? (
          <pre className="text-[11px] bg-white border border-gray-200 rounded-lg p-2 overflow-auto max-h-56 whitespace-pre-wrap break-all">{JSON.stringify(o.woo.raw, null, 2)}</pre>
        ) : <div className="text-xs text-gray-400">Bu sipariş için Woo kaynak kaydı gelmedi.</div>}
      </div>
    </div>
  );
}

export default function AttributionTracking() {
  const { selectedBrand } = useAuth();
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [includeTest, setIncludeTest] = useState(true);
  const [page, setPage] = useState(0);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [site, setSite] = useState(null); // site-status yanıtı; null = yükleniyor

  const brandId = selectedBrand?.id;
  const brandName = selectedBrand?.name || 'bu marka';
  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true); setError(null);
    const qs = `brandId=${brandId}&from=${from}&to=${to}&includeTest=${includeTest ? 1 : 0}`;
    try {
      const [rs, ro] = await Promise.all([
        fetch(`${BASE}/summary?${qs}`, { credentials: 'include' }),
        fetch(`${BASE}/orders?${qs}&limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`, { credentials: 'include' }),
      ]);
      if (!rs.ok || !ro.ok) throw new Error(`Sunucu yanıtı ${!rs.ok ? rs.status : ro.status}`);
      const js = await rs.json(); const jo = await ro.json();
      setSummary(js.data); setOrders(jo.data || []); setTotal(jo.meta?.total || 0);
    } catch (e) {
      setError(e.message || 'Veri alınamadı');
    } finally { setLoading(false); }
  }, [brandId, from, to, includeTest, page]);

  useEffect(() => {
    if (!brandId) return undefined;
    let alive = true;
    setSite(null);
    fetch(`${BASE}/site-status?brandId=${brandId}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => { if (alive) setSite(j.data || { configured: false }); })
      .catch(() => { if (alive) setSite({ unknown: true }); });
    return () => { alive = false; };
  }, [brandId]);
  useEffect(() => { if (site && site.configured === false) return; load(); }, [load, site]);
  useEffect(() => { setPage(0); setOpen(null); }, [from, to, includeTest]);

  const t = summary?.totals || {};
  const consentRows = summary?.consent || [];
  const denied = consentRows.find((c) => c.consent === 'denied')?.orders || 0;
  const consentTotal = consentRows.reduce((a, c) => a + c.orders, 0);
  const rec = summary?.recovered_by_persistent || {};
  const channels = summary?.channels || [];
  const maxRev = useMemo(() => Math.max(1, ...channels.map((c) => c.revenue_valid)), [channels]);
  const woo = summary?.woo_comparison;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const notConfigured = site && site.configured === false;
  const empty = !loading && !error && (t.orders || 0) === 0;

  if (!brandId || !site) {
    return <div className="p-6 flex justify-center text-gray-400"><Loader2 className="animate-spin" /></div>;
  }
  if (notConfigured) {
    return (
      <div className="p-4 sm:p-6 max-w-[1500px] mx-auto space-y-5">
        <h1 className="text-xl font-semibold text-gray-900">Kaynak Takibi</h1>
        <div className="bg-white border border-dashed border-gray-300 rounded-xl py-16 px-6 text-center">
          <Inbox className="mx-auto text-gray-300 mb-3" size={36} />
          <div className="text-base font-medium text-gray-800">Bu marka için kaynak takibi kurulmamış</div>
          <p className="text-sm text-gray-500 mt-2 max-w-xl mx-auto">{brandName} için kayıtlı bir site tanımı yok; bu yüzden sipariş ve olay toplanmıyor. Kurulum yapıldığında bu sayfa dolacak.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-[1500px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900">Kaynak Takibi</h1>
          <p className="text-sm text-gray-500 max-w-2xl">
            Birinci taraf veriyle her siparişe tek bir kaynak atanır: kalıcı kayıt (A) ve oturum kaydı (B) birleştirilir, sipariş iki kanala sayılmaz.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 bg-white" />
          <span className="text-gray-400">-</span>
          <input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 bg-white" />
          {[7, 30, 90].map((n) => (
            <button key={n} onClick={() => { setFrom(daysAgo(n - 1)); setTo(daysAgo(0)); }} className="px-2 py-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50">Son {n} gün</button>
          ))}
          <label className="flex items-center gap-1.5 pl-2 cursor-pointer select-none" title="Prototip testi sırasında açık bırakıldı">
            <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} />
            Test siparişlerini göster
          </label>
          <Link to="/cro/kaynak-takibi/veri" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 whitespace-nowrap">Toplanan veri</Link>
          <Link to="/cro/kaynak-takibi/kurulum" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 whitespace-nowrap">GTM kurulumu</Link>
          <button onClick={load} className="p-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50" title="Yenile"><RotateCcw size={14} /></button>
        </div>
      </div>

      {error && <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">Veri alınamadı: {error}</div>}

      {loading && !summary ? (
        <div className="flex items-center justify-center py-24 text-gray-400"><Loader2 className="animate-spin" /></div>
      ) : empty ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl py-16 px-6 text-center">
          <Inbox className="mx-auto text-gray-300 mb-3" size={36} />
          <div className="text-base font-medium text-gray-800">Bu aralıkta kaynağı kaydedilmiş sipariş yok</div>
          <p className="text-sm text-gray-500 mt-2 max-w-xl mx-auto">
            Siparişler {brandName} sitesinden oluştukça buraya düşer; her biri için kalıcı kayıt (A), oturum kaydı (B) ve atanan tek kaynak listelenir.
            Tarih aralığını genişletmeyi{!includeTest ? ' ya da test siparişlerini göstermeyi' : ''} deneyebilirsin.
          </p>
        </div>
      ) : summary && (
        <>
          <div className={`grid grid-cols-2 lg:grid-cols-5 gap-3 ${loading ? 'opacity-60' : ''}`}>
            <Kpi icon={ShoppingBag} label="Sipariş (geçerli)" value={nf.format(t.orders_valid || 0)} sub={`geçerli ${nf.format(t.orders_valid || 0)} · iptaller dahil ${nf.format(t.orders || 0)}`} />
            <Kpi icon={Wallet} label="Ciro (geçerli)" value={money(t.revenue_valid)} sub={`geçerli ${money(t.revenue_valid)} · iptaller dahil ${money(t.revenue)}`} />
            <Kpi icon={ShieldOff} label="İzinsiz siparişlerin payı" value={pct(denied, consentTotal)} sub={`${nf.format(denied)} / ${nf.format(consentTotal)} sipariş çerez izni vermedi`} />
            <Kpi icon={Undo2} label="Kalıcı saklama ile kurtarılan" value={`${nf.format(rec.orders_valid || 0)} sipariş`} tone="text-indigo-700"
              sub={`geçerli ${money(rec.revenue_valid)} · iptaller dahil ${nf.format(rec.orders || 0)} sipariş, ${money(rec.revenue)}. Oturum kaynağı doğrudan görünüyordu, 30 günlük kalıcı kayıt kampanyayı gösterdi.`} />
            <Kpi icon={GitCompareArrows} label="A ≠ B uyuşmazlık" value={nf.format(summary.ab_mismatch || 0)} sub="Kalıcı ve oturum kaydı farklı kanal gösteren sipariş" />
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-sm font-medium text-gray-900 mb-3">Kanal bazında tekilleştirilmiş siparişler</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-3 font-medium">Kanal</th>
                    <th className="py-2 pr-3 font-medium w-[34%]">Ciro payı (geçerli)</th>
                    <th className="py-2 pr-3 font-medium text-right">Sipariş (geçerli)</th>
                    <th className="py-2 pr-3 font-medium text-right">Ciro (geçerli)</th>
                    <th className="py-2 pr-3 font-medium text-right">Pay</th>
                    <th className="py-2 pr-3 font-medium text-right">Sipariş (toplam)</th>
                    <th className="py-2 font-medium text-right">Ciro (toplam)</th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((c) => (
                    <tr key={c.channel} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 pr-3"><span className="inline-flex items-center gap-1.5 font-medium text-gray-900"><span className="w-2 h-2 rounded-full" style={{ background: chColor(c.channel) }} />{chLabel(c.channel)}</span></td>
                      <td className="py-2 pr-3"><div className="h-2 rounded bg-gray-100"><div className="h-2 rounded" style={{ width: `${(c.revenue_valid / maxRev) * 100}%`, background: chColor(c.channel) }} /></div></td>
                      <td className="py-2 pr-3 text-right tabular-nums">{nf.format(c.orders_valid)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{money(c.revenue_valid)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{pct(c.revenue_valid, t.revenue_valid)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{nf.format(c.orders)}</td>
                      <td className="py-2 text-right tabular-nums text-gray-500">{money(c.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <FunnelSection from={from} to={to} includeTest={includeTest} />

          <div className={`bg-white border border-gray-200 rounded-xl overflow-hidden ${loading ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="text-sm font-medium text-gray-900">Siparişler <span className="text-gray-400 font-normal tabular-nums">({nf.format(total)})</span></div>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <button disabled={page === 0} onClick={() => setPage(page - 1)} className="px-2 py-1 border border-gray-300 rounded-lg disabled:opacity-40">Önceki</button>
                <span className="tabular-nums">{page + 1} / {pages}</span>
                <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="px-2 py-1 border border-gray-300 rounded-lg disabled:opacity-40">Sonraki</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50 border-b border-gray-200">
                    <th className="w-6" />
                    <th className="py-2 pr-3 font-medium">Sipariş</th>
                    <th className="py-2 pr-3 font-medium">Durum</th>
                    <th className="py-2 pr-3 font-medium text-right">Tutar</th>
                    <th className="py-2 pr-3 font-medium">İzin</th>
                    <th className="py-2 pr-3 font-medium">Atanan kaynak</th>
                    <th className="py-2 pr-3 font-medium">A (kalıcı)</th>
                    <th className="py-2 pr-3 font-medium">B (oturum)</th>
                    <th className="py-2 pr-3 font-medium">İlk dokunuş</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const isOpen = open === o.order_id;
                    const canc = CANCELLED.includes(String(o.status || '').toLowerCase());
                    const cs = CONSENT[o.consent] || CONSENT.unknown;
                    const og = ORIGIN[o.final?.origin];
                    return (
                      <React.Fragment key={o.order_id}>
                        <tr onClick={() => setOpen(isOpen ? null : o.order_id)} className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 align-top ${canc ? 'text-gray-400' : ''}`}>
                          <td className="pl-2 pt-3">{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                          <td className="py-2.5 pr-3">
                            <div className="font-medium text-gray-900 tabular-nums">#{o.order_id}</div>
                            <div className="text-[11px] text-gray-500">{fmtDate(o.created_at)}</div>
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {o.is_test && <Badge cls="bg-amber-50 text-amber-700 border-amber-200">Test</Badge>}
                              {o.recovered_by_persistent && <Badge cls="bg-indigo-50 text-indigo-700 border-indigo-200" title="Oturum kaynağı doğrudan, kalıcı kayıt kampanyayı gösterdi">Kurtarıldı</Badge>}
                            </div>
                          </td>
                          <td className="py-2.5 pr-3">{canc ? <Badge cls="bg-rose-50 text-rose-700 border-rose-200">{STATUS_TR[String(o.status).toLowerCase()] || o.status}</Badge> : <span className="text-gray-700">{STATUS_TR[String(o.status || '').toLowerCase()] || o.status || '-'}</span>}</td>
                          <td className={`py-2.5 pr-3 text-right tabular-nums whitespace-nowrap ${canc ? 'line-through' : 'text-gray-900'}`}>{money(o.total, o.currency)}</td>
                          <td className="py-2.5 pr-3"><Badge cls={cs.cls}>{cs.label}</Badge></td>
                          <td className="py-2.5 pr-3">
                            <SourceCell ch={o.final?.channel} o={o.final} />
                            {og && <div className="mt-1"><Badge cls={og.cls} title="Atamanın geldiği kayıt">{og.label}</Badge></div>}
                          </td>
                          <td className="py-2.5 pr-3"><SourceCell ch={o.a?.channel} o={o.a} muted /></td>
                          <td className="py-2.5 pr-3"><SourceCell ch={o.b?.channel} o={o.b} muted /></td>
                          <td className="py-2.5 pr-3"><SourceCell ch={o.first?.channel} o={o.first} muted /></td>
                        </tr>
                        {isOpen && <tr><td colSpan={9} className="p-0"><Detail o={o} /></td></tr>}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-sm font-medium text-gray-900 mb-1">WooCommerce kaynak kaydı ile karşılaştırma</div>
            {woo && woo.orders_with_woo_source > 0 ? (
              <>
                <p className="text-xs text-gray-500 mb-3">{nf.format(woo.orders_with_woo_source)} siparişte Woo kaynak kaydı geldi. Woo'nun yazdığı kaynak ile bizim atadığımız kanal yan yana.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-gray-500 border-b border-gray-200"><th className="py-2 pr-3 font-medium">Woo kaynağı</th><th className="py-2 pr-3 font-medium">Atanan kanal</th><th className="py-2 font-medium text-right">Sipariş</th></tr></thead>
                    <tbody>
                      {woo.pairs.map((p, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0">
                          <td className="py-1.5 pr-3">{p.woo_source || <span className="text-gray-400">kaynak alanı boş</span>}</td>
                          <td className="py-1.5 pr-3">{chLabel(p.final_channel)}</td>
                          <td className="py-1.5 text-right tabular-nums">{nf.format(p.orders)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : <p className="text-xs text-gray-500">Bu aralıktaki siparişler için Woo kaynak kaydı gelmedi.</p>}
          </div>
        </>
      )}
      <SiteInfo site={site} />
    </div>
  );
}
